"use client";

import { useEffect, useRef, useState, type ComponentType } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Facebook,
  Instagram,
  Linkedin,
  MessageCircle,
  Share2,
  Youtube,
  Github,
} from "lucide-react";
import { useAppSelector } from "@/store/hooks";

type SocialIcon = ComponentType<{ size?: number; "aria-hidden"?: boolean }>;

const XIcon = ({ size = 15 }: { size?: number; "aria-hidden"?: boolean }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.964 6.817H1.684l7.73-8.835L1.258 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const socialPlatforms: Array<{ key: string; label: string; icon: SocialIcon; brandColor: string }> = [
  { key: "facebook", label: "Facebook", icon: Facebook, brandColor: "#1877F2" },
  { key: "twitter", label: "X (Twitter)", icon: XIcon, brandColor: "#000000" },
  { key: "instagram", label: "Instagram", icon: Instagram, brandColor: "#E1306C" },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, brandColor: "#0A66C2" },
  { key: "youtube", label: "YouTube", icon: Youtube, brandColor: "#FF0000" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, brandColor: "#25D366" },
  // { key: "github", label: "GitHub", icon: Github, brandColor: "#333333" },
];

const getSocialUrl = (value: unknown) => {
  if (typeof value !== "string" || !value.trim() || value.trim() === "#") return null;
  const url = value.trim();
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

export default function FloatingSocialLinks() {
  const { data: settings, loading } = useAppSelector((state) => state.landing.generalSettings);
  const { mode } = useAppSelector((state) => state.theme);
  const [open, setOpen] = useState(false);
  const [supportsHover, setSupportsHover] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const settingsData = Array.isArray(settings) ? settings[settings.length - 1] : ((settings as any)?.data || settings);
  const socialLinks = settingsData?.socialLinks;
  const availableLinks = socialPlatforms.filter(({ key }) => {
    return Boolean(getSocialUrl(socialLinks?.[key as keyof typeof socialLinks]));
  });

  useEffect(() => {
    const hoverQuery = window.matchMedia("(hover: hover)");
    const updateHoverSupport = () => setSupportsHover(hoverQuery.matches);
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!widgetRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    updateHoverSupport();
    hoverQuery.addEventListener("change", updateHoverSupport);
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      hoverQuery.removeEventListener("change", updateHoverSupport);
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  if (loading || availableLinks.length === 0) return null;

  return (
    <div
      ref={widgetRef}
      onMouseEnter={() => supportsHover && setOpen(true)}
      onMouseLeave={() => supportsHover && setOpen(false)}
      className="fixed top-1/2 left-0 z-50 -translate-y-1/2"
    >
      <div
        className={`flex flex-col items-stretch overflow-hidden rounded-r-xl border border-l-0 shadow-lg backdrop-blur-md ${
          mode === "dark"
            ? "border-white/10 bg-gray-900/95"
            : "border-gray-200 bg-white/95"
        }`}
      >
        <AnimatePresence>
          {open && (
            <motion.nav
              aria-label="Social media links"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.18 }}
              className="flex flex-col gap-0.5 p-1"
            >
              {availableLinks.map(({ key, label, icon: Icon, brandColor }, index) => {
                const href = getSocialUrl(socialLinks?.[key as keyof typeof socialLinks]);
                return (
                  <motion.a
                    key={key}
                    href={href || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Visit our ${label} page`}
                    title={label}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: reduceMotion ? 0 : index * 0.03 }}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.94 }}
                    style={{ color: key === "twitter" && mode === "dark" ? "#FFFFFF" : brandColor }}
                    className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors sm:h-10 sm:w-10 ${
                      mode === "dark" ? "bg-white/5 hover:bg-white/10" : "bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <Icon size={16} aria-hidden={true} />
                  </motion.a>
                );
              })}
            </motion.nav>
          )}
        </AnimatePresence>
        <button
          type="button"
          aria-label={open ? "Close social media links" : "Open social media links"}
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          className={`flex h-11 w-11 items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#8B31FF] sm:h-12 sm:w-12 ${
            mode === "dark"
              ? "bg-gray-900 text-white hover:bg-gray-800"
              : "bg-white text-gray-800 hover:text-[#8B31FF]"
          }`}
        >
          <Share2 size={17} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}